import { FC, useContext, useState, Dispatch, SetStateAction } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import { useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';

import { StepHeader } from 'dibs-elements/exports/StepHeader';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import CirclePlus from 'dibs-icons/exports/legacy/CirclePlus';
import CheckmarkCircle from 'dibs-icons/exports/legacy/CheckmarkCircle';
import InputsWrapper from './sharedComponents/InputsWrapper';
import FormButton from './sharedComponents/FormButton';

import Currency from './sharedComponents/Currency';

import SellerFormContext from './SellerFormContext';

import { FormDataConsignorInventoryDetails } from './SellerApplicationFormReducer';
import { updateSellerApplicationLeadMutation } from './mutations/updateSellerApplicationLead';

import styles from './styles/SellerApplicationFormInventoryShared.scss';
import dibsCss from 'dibs-css';

const MAX_ITEMS = 3;

const messages = defineMessages({
    title: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsInventoryDetails.title',
        defaultMessage: 'Add details for up to 3 items',
    },
    itemsAmountPlaceholder: {
        id: 'SellerApplicationForm.SellerApplicationFormConsignorsInventoryDetails.itemsAmountPlaceholder',
        defaultMessage: 'How many items are you looking to sell?',
    },
});

type Props = {
    setFormError: Dispatch<SetStateAction<boolean>>;
};

const SellerApplicationFormConsignorsInventoryDetails: FC<Props> = ({ setFormError }) => {
    const intl = useIntl();
    const { state, dispatch } = useContext(SellerFormContext);
    const environment = useRelayEnvironment();
    const [isLoading, setIsLoading] = useState(false);
    const { estimatedInventorySize, averageItemPriceCurrency } =
        state.formDataConsignorInventory || {};
    const consignorItems = state.consignorItems;
    const consignorItemsLength = consignorItems.length;

    const onSubmit = async (): Promise<void> => {
        const { leadId, formDataConsignorInventory, consignorItems: items } = state || {};

        try {
            setIsLoading(true);
            await updateSellerApplicationLeadMutation(environment, {
                recordId: leadId || '',
                estimatedInventorySize: String(
                    formDataConsignorInventory?.estimatedInventorySize ?? ''
                ),
                currencyISOCode: formDataConsignorInventory?.averageItemPriceCurrency,
                consignorItems: (items || []).map(item => {
                    const {
                        vertical,
                        subcategory,
                        creator,
                        howItemWasAcquired,
                        condition,
                        price,
                        imageFiles,
                    } = item || {};
                    return {
                        category: vertical,
                        subCategory: subcategory?.label,
                        creator: creator?.label,
                        howItemWasAcquired,
                        condition,
                        price,
                        imageFile: imageFiles[0],
                    };
                }),
            });
        } catch (err) {
            setIsLoading(false);
            setFormError(true);
            if (err instanceof Error) {
                throw err;
            }
        }
        setIsLoading(false);
        dispatch({ type: 'SET_STEP', step: 'CONSIGNORS_CONFIRMATION' });
    };

    const onEditItem = (index: number): void => {
        dispatch({ type: 'SET_CONSIGNOR_ACTIVE_ITEM_INDEX', itemIndex: index });
        dispatch({ type: 'SET_STEP', step: 'CONSIGNORS_STEP_ITEM_UPLOAD' });
    };

    const addAnotherItem = (): void => {
        dispatch({
            type: 'SET_CONSIGNOR_ACTIVE_ITEM_INDEX',
            itemIndex: consignorItemsLength,
        });
        dispatch({ type: 'ADD_CONSIGNOR_ITEM' });
        dispatch({ type: 'SET_STEP', step: 'CONSIGNORS_STEP_ITEM_UPLOAD' });
    };

    const setFormField = (field: keyof FormDataConsignorInventoryDetails, value: string): void => {
        dispatch({
            type: 'SET_FORM_CONSIGNOR_INVENTORY_DETAILS',
            formDataConsignorInventory: { [field]: value },
        });
    };

    return (
        <div>
            <StepHeader title={intl.formatMessage(messages.title)} totalSteps={2} currentStep={2} />
            <div>
                <InputsWrapper>
                    <BasicSelect
                        dataTn="items-amount"
                        placeholder={intl.formatMessage(messages.itemsAmountPlaceholder)}
                        options={[
                            { label: '1 - 5', value: '1-5' },
                            { label: '5 - 10', value: '5-10' },
                            { label: '10+', value: '10-' },
                        ]}
                        onChange={e => setFormField('estimatedInventorySize', e.target.value)}
                        value={estimatedInventorySize}
                        disabled={isLoading}
                    />
                    <Currency
                        dataTn="currency-select"
                        onChange={e => setFormField('averageItemPriceCurrency', e.target.value)}
                        value={averageItemPriceCurrency || ''}
                        disabled={isLoading}
                    />
                </InputsWrapper>
                {consignorItems.map((item, i) => (
                    <Link
                        key={`${item?.creator}-${i}`}
                        className={styles.itemLink}
                        onClick={() => onEditItem(i)}
                    >
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.sassyFontBody
                            )}
                        >
                            <CheckmarkCircle className={styles.icon} />
                            {item?.creator?.label} {item?.subcategory?.label}
                        </div>
                        <ArrowRight className={styles.icon} />
                    </Link>
                ))}
                {!!consignorItemsLength && consignorItemsLength < MAX_ITEMS && (
                    <Button
                        fullWidth
                        type="secondaryAlt"
                        className={dibsCss.mbSmall}
                        onClick={addAnotherItem}
                        disabled={isLoading}
                    >
                        <FormattedMessage
                            id="SellerApplicationForm.SellerApplicationFormConsignorsInventoryDetails.addAnotherItemLabel"
                            defaultMessage="Add another Item (Optional)"
                        />
                    </Button>
                )}
                {!consignorItemsLength && (
                    <Link
                        className={styles.itemLink}
                        onClick={() => {
                            dispatch({ type: 'SET_STEP', step: 'CONSIGNORS_STEP_ITEM_UPLOAD' });
                        }}
                    >
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.sassyFontBody
                            )}
                        >
                            <CirclePlus className={styles.icon} />
                            <FormattedMessage
                                id="SellerApplicationForm.SellerApplicationFormConsignorsInventoryDetails.addItemLabel"
                                defaultMessage="Add Item Details"
                            />
                        </div>
                        <ArrowRight className={styles.icon} />
                    </Link>
                )}
                <FormButton
                    isLoading={isLoading}
                    onSubmit={onSubmit}
                    disabled={
                        !estimatedInventorySize ||
                        !averageItemPriceCurrency ||
                        !consignorItemsLength
                    }
                    submitCopy={
                        <FormattedMessage
                            id="SellerApplicationForm.SellerApplicationFormConsignorsInventoryDetails.submit"
                            defaultMessage="Submit application"
                        />
                    }
                />
            </div>
        </div>
    );
};

export default SellerApplicationFormConsignorsInventoryDetails;
